import axios, {
  AxiosError,
  type AxiosInstance,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
} from 'axios';

import {
  leaveAuthBreadcrumb,
  normalizeError,
  notifyAuthError,
  summarizePayload,
} from '@/helpers/bugsnag';

const options = {
  baseURL: '/api',
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
    'X-Requested-With': 'XMLHttpRequest',
    'X-User-Agent': 'erank-app/3.0',
  },
};

const instance = axios.create({
  ...options,
  withCredentials: true,
  withXSRFToken: true,
});

const publicInstance = axios.create({
  ...options,
});

const customInstance = axios.create({
  ...options,
  baseURL: '/',
  withCredentials: true,
  withXSRFToken: true,
});

type AuthRequestConfig = InternalAxiosRequestConfig;
type AuthRequestConfigWithController = AuthRequestConfig & {
  __rateLimitAbortController?: AbortController;
};

const defaultRetryAfterSeconds = 120;
const pendingAbortControllers = new Set<AbortController>();
let rateLimitedUntilMs = 0;

function getRemainingRateLimitSeconds(): number {
  return Math.max(0, Math.ceil((rateLimitedUntilMs - Date.now()) / 1000));
}

function isRateLimitedActive(): boolean {
  return getRemainingRateLimitSeconds() > 0;
}

function cleanupAbortController(config?: AuthRequestConfig): void {
  const requestConfig = config as AuthRequestConfigWithController | undefined;
  const controller = requestConfig?.__rateLimitAbortController;

  if (!controller) {
    return;
  }

  pendingAbortControllers.delete(controller);
  delete requestConfig.__rateLimitAbortController;
}

function activateRateLimitLock(retryAfterSeconds: number): void {
  rateLimitedUntilMs = Date.now() + Math.max(0, retryAfterSeconds) * 1000;

  pendingAbortControllers.forEach(controller => {
    controller.abort('Rate limited: stopping in-flight requests');
  });
  pendingAbortControllers.clear();
}

function createRateLimitAxiosError(config: AuthRequestConfig, retryAfterSeconds: number): AxiosError {
  return new AxiosError(
    'Request blocked due to an active rate limit.',
    'ERR_RATE_LIMITED',
    config,
    undefined,
    {
      config,
      data: {
        message: 'Too many requests. Please wait before retrying.',
      },
      headers: {
        'retry-after': String(Math.max(0, retryAfterSeconds)),
      },
      status: 429,
      statusText: 'Too Many Requests',
    } as AxiosResponse,
  );
}

function parseRetryAfterToSeconds(headerValue: unknown): number {
  if (typeof headerValue === 'number' && Number.isFinite(headerValue)) {
    return Math.max(0, Math.floor(headerValue));
  }

  if (typeof headerValue !== 'string') {
    return defaultRetryAfterSeconds;
  }

  const trimmed = headerValue.trim();
  if (!trimmed) {
    return defaultRetryAfterSeconds;
  }

  const asSeconds = Number.parseInt(trimmed, 10);
  if (Number.isFinite(asSeconds) && !Number.isNaN(asSeconds)) {
    return Math.max(0, asSeconds);
  }

  const retryTimestamp = Date.parse(trimmed);
  if (Number.isNaN(retryTimestamp)) {
    return defaultRetryAfterSeconds;
  }

  return Math.max(0, Math.ceil((retryTimestamp - Date.now()) / 1000));
}

function redirectToRateLimitPage(error: AxiosError): void {
  const statusCode = error.response?.status;
  const currentPath = window.location.pathname;

  if (statusCode !== 429 || currentPath === '/high-activity') {
    return;
  }

  const currentRoute = `${window.location.pathname}${window.location.search}${window.location.hash}`;
  window.sessionStorage.setItem('last_non_429_route', currentRoute);

  const retryAfterHeader = error.response?.headers?.['retry-after'];
  const retryAfter = parseRetryAfterToSeconds(retryAfterHeader);
  const target = `/high-activity?retry_after=${retryAfter}&return_to=${encodeURIComponent(currentRoute)}`;

  window.location.assign(target);
}

function isForcedLogout(error: AxiosError): boolean {
  const data = error.response?.data as { code?: string } | undefined;
  const code = data?.code;

  return code === 'SESSION_REVOKED';
}

/**
 * Clears the one-time guard so a future genuine session-revoked event can show the page again.
 * Call this after the user successfully authenticates.
 */
function clearSessionLimitShown(): void {
  window.sessionStorage.removeItem(SESSION_LIMIT_SHOWN_KEY);
}

const SESSION_LIMIT_SHOWN_KEY = 'session_limit_page_shown';

/**
 * Returns true if the one-time session-limit page has been shown for the
 * current "session revoked" event. Used to guard direct visits to the route.
 */
function wasSessionLimitShown(): boolean {
  return Boolean(window.sessionStorage.getItem(SESSION_LIMIT_SHOWN_KEY));
}

function redirectToSessionLimitPage(): void {
  if (wasSessionLimitShown()) {
    return;
  }

  const currentPath = window.location.pathname;

  if (currentPath === '/signed-out') {
    return;
  }

  window.sessionStorage.setItem(SESSION_LIMIT_SHOWN_KEY, '1');

  const currentRoute = `${window.location.pathname}${window.location.search}${window.location.hash}`;
  window.sessionStorage.setItem('last_non_session_limit_route', currentRoute);

  window.location.assign('/signed-out');
}

function attachInterceptors(client: AxiosInstance, clientName: string): void {
  client.interceptors.request.use((config: AuthRequestConfig) => {
    if (isRateLimitedActive()) {
      const retryAfterSeconds = getRemainingRateLimitSeconds();
      return Promise.reject(createRateLimitAxiosError(config, retryAfterSeconds));
    }

    const requestConfig = config as AuthRequestConfigWithController;
    if (!requestConfig.signal) {
      const controller = new AbortController();
      requestConfig.signal = controller.signal;
      requestConfig.__rateLimitAbortController = controller;
      pendingAbortControllers.add(controller);
    }

    leaveAuthBreadcrumb('Axios request dispatched', {
      client: clientName,
      hasPayload: Boolean(config.data),
      method: config.method?.toUpperCase(),
      payloadSummary: summarizePayload(config.data),
      url: config.url,
    });

    return config;
  });

  client.interceptors.response.use(
    response => {
      const config = response.config as AuthRequestConfig;
      cleanupAbortController(config);

      leaveAuthBreadcrumb('Axios response received', {
        client: clientName,
        method: config.method?.toUpperCase(),
        status: response.status,
        url: config.url,
      });

      return response;
    },
    (error: AxiosError) => {
      const config = error.config as AuthRequestConfig | undefined;
      cleanupAbortController(config);

      if (error.response?.status === 429) {
        const retryAfter = parseRetryAfterToSeconds(error.response.headers?.['retry-after']);
        activateRateLimitLock(retryAfter);
      }

      leaveAuthBreadcrumb('Axios request failed', {
        client: clientName,
        isNetworkError: !error.response,
        method: config?.method?.toUpperCase(),
        status: error.response?.status,
        url: config?.url,
      });

      notifyAuthError(error, {
        client: clientName,
        method: config?.method?.toUpperCase(),
        payloadSummary: summarizePayload(config?.data),
        status: error.response?.status,
        url: config?.url,
      });

      if (isForcedLogout(error)) {
        redirectToSessionLimitPage();
      } else {
        redirectToRateLimitPage(error);
      }

      return Promise.reject(normalizeError(error, 'Axios request failed', 'AxiosError'));
    },
  );
}

attachInterceptors(instance, 'private');
attachInterceptors(publicInstance, 'public');
attachInterceptors(customInstance, 'custom');

async function sanctumCookie(): Promise<AxiosResponse> {
  leaveAuthBreadcrumb('Fetching Sanctum CSRF cookie');

  try {
    const response = await customInstance.get('/sanctum/csrf-cookie');

    leaveAuthBreadcrumb('Sanctum CSRF cookie fetched', {
      status: response.status,
    });

    return response;
  } catch (error) {
    notifyAuthError(error, {
      action: 'sanctumCookie',
    });

    throw normalizeError(error, 'Failed to fetch Sanctum CSRF cookie', 'AxiosError');
  }
}

export {
  instance as axios,
  customInstance as baseAxios,
  clearSessionLimitShown,
  publicInstance as publicAxios,
  sanctumCookie,
  wasSessionLimitShown,
};
